import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

const EMOJI_MAP   = { angry: '😠', sad: '😢', neutral: '😐', happy: '😊', excited: '🤩' };
const COLORS      = { angry: '#ef4444', sad: '#f97316', neutral: '#94a3b8', happy: '#22c55e', excited: '#8b5cf6' };
const SCORE_COLOR = (s) => parseFloat(s) >= 4 ? '#22c55e' : parseFloat(s) >= 3 ? '#f59e0b' : '#ef4444';

function StatCard({ icon, label, value, sub, color, onClick }) {
    return (
        <div
            className="card"
            onClick={onClick}
            style={{
                flex: '1 1 140px',
                padding: '18px 16px',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'transform .15s',
                borderLeft: color ? `3px solid ${color}` : undefined,
            }}
            onMouseOver={e => onClick && (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseOut={e => onClick && (e.currentTarget.style.transform = 'none')}
        >
            <div style={{ fontSize: 24, marginBottom: 6 }}>{icon}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: color || 'var(--text)' }}>{value ?? '—'}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
            {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
        </div>
    );
}

function SentimentBar({ counts }) {
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    if (!total) return <div style={{ height: 10, background: 'var(--border)', borderRadius: 5 }} />;
    return (
        <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', gap: 1 }}>
            {Object.entries(counts).map(([k, v]) =>
                v > 0 ? (
                    <div key={k} title={`${EMOJI_MAP[k]} ${k}: ${v}`}
                         style={{ flex: v, background: COLORS[k], minWidth: 3 }} />
                ) : null
            )}
        </div>
    );
}

function TrendChart({ data }) {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data.map(d => d.count), 1);
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 60, paddingTop: 8 }}>
            {data.map((d, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{d.count || ''}</div>
                    <div style={{
                        width: '100%',
                        height: `${Math.max(6, (d.count / max) * 44)}px`,
                        background: d.count > 0 ? 'var(--primary)' : 'var(--border)',
                        borderRadius: '3px 3px 0 0',
                        opacity: i === data.length - 1 ? 1 : 0.6,
                    }} />
                    <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{d.label}</div>
                </div>
            ))}
        </div>
    );
}

function RankRow({ rank, name, sub, count, avgScore, sentimentCounts }) {
    const rankStyle = [
        { background: '#fbbf24', color: '#fff' },
        { background: '#94a3b8', color: '#fff' },
        { background: '#d97706', color: '#fff' },
    ][rank - 1] || { background: 'var(--bg)', color: 'var(--text-muted)' };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '0.5px solid var(--border)' }}>
            <div style={{ ...rankStyle, width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                {rank}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</div>}
                {sentimentCounts && (
                    <div style={{ marginTop: 4 }}><SentimentBar counts={sentimentCounts} /></div>
                )}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--primary)' }}>{count}</div>
                {avgScore && (
                    <div style={{ fontSize: 11, color: SCORE_COLOR(avgScore), fontWeight: 600 }}>{avgScore}/5</div>
                )}
            </div>
        </div>
    );
}

export default function HomeTab({ onNavigate }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/home');
            setData(res.data.data);
        } catch (err) {
            setError('Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const getGreeting = () => {
        const h = new Date().getHours();
        if (h < 12) return 'Good morning';
        if (h < 17) return 'Good afternoon';
        return 'Good evening';
    };

    if (loading) return <div className="spinner" style={{ marginTop: 60 }} />;
    if (error) return <div className="alert alert-error" style={{ marginTop: 20 }}>{error}</div>;
    if (!data) return null;

    const { today, thisWeek, thisMonth, allTime, pendingAnalysis, totalCustomers, newCustomersToday, totalStaff, totalLocations, topStaff, topLocations, trend7Days, recentResponses } = data;

    return (
        <div style={{ paddingBottom: 40 }}>

            {/* ── Greeting ── */}
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ marginBottom: 4 }}>Here's what's happening at {user?.businessName} today.</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Let's see how your customers are feeling today.</p>
            </div>

            {/* ── Today's pulse ── */}
            <div style={{ marginBottom: 25 }}>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-muted)', marginBottom: 12 }}>Today's Pulse</p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <StatCard icon="💬" label="Responses today" value={today.total} sub={`${today.manualCount} emoji · ${today.audioCount} audio`} color="var(--primary)" onClick={() => onNavigate('responses')} />
                    <StatCard
                        icon={today.dominantEmoji || '😐'}
                        label="Overall mood"
                        value={today.dominantSentiment ? today.dominantSentiment.charAt(0).toUpperCase() + today.dominantSentiment.slice(1) : '—'}
                        sub={today.avgScore ? `Avg score: ${today.avgScore}/5` : 'No scored feedback yet'}
                        color={today.dominantSentiment ? COLORS[today.dominantSentiment] : undefined}
                    />
                    <StatCard icon="⏳" label="Pending sessions" value={today.pendingSessions} sub="Started but not submitted" color={today.pendingSessions > 0 ? '#f97316' : undefined} />
                    {pendingAnalysis > 0 && (
                        <StatCard icon="🔄" label="Audio analysing" value={pendingAnalysis} sub="Transcription in progress" color="#8b5cf6" />
                    )}
                </div>
            </div>

            {/* ── Today sentiment bar ── */}
            {today.total > 0 && (
                <div className="card" style={{marginBottom: 28, padding: '14px 18px'}}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 10
                    }}>
                        <span style={{fontSize: 13, fontWeight: 600}}>Today's sentiment breakdown</span>
                    </div>
                    <SentimentBar counts={today.sentimentCounts}/>
                    <div style={{display: 'flex', gap: 8, marginTop: 10}}>
                        {Object.entries(today.sentimentCounts).map(([k, v]) => v > 0 && (
                            <span key={k} style={{
                                fontSize: 11,
                                padding: '2px 8px',
                                borderRadius: 10,
                                background: COLORS[k] + '20',
                                color: COLORS[k],
                                fontWeight: 600,
                                textAlign: 'center'
                            }}>
                                  {EMOJI_MAP[k]} {v}
                                </span>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Period stats ── */}
            <div style={{marginBottom: 24}}>
                <p style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '.07em',
                    color: 'var(--text-muted)',
                    marginBottom: 12
                }}>Overview</p>
                <div style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
                    <StatCard icon="📅" label="This week" value={thisWeek.total} sub="Responses"/>
                    <StatCard icon="🗓️" label="This month" value={thisMonth.total} sub="Responses" />
                    <StatCard icon="📊" label="All time" value={allTime.total} sub="Responses" onClick={() => onNavigate('responses')} />
                    <StatCard icon="👥" label="Customers" value={totalCustomers} sub={newCustomersToday > 0 ? `+${newCustomersToday} today` : 'Opted-in contacts'} color={newCustomersToday > 0 ? '#22c55e' : undefined} onClick={() => onNavigate('customers')} />
                    <StatCard icon="👤" label="Active staff" value={totalStaff} sub="Feedback takers" onClick={() => onNavigate('staff')} />
                    <StatCard icon="📍" label="Locations" value={totalLocations} sub="Active branches" onClick={() => onNavigate('locations')} />
                </div>
            </div>

            {/* ── 7-day trend ── */}
            <div className="card" style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>7-day response trend</span>
                    <button
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: 11 }}
                        onClick={() => onNavigate('responses')}
                    >All →</button>
                </div>
                <TrendChart data={trend7Days} />
            </div>

            {/* ── Top staff + Top locations ── */}
            <div className={'top-staff-location'} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>

                {/* Top staff */}
                <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>🏆 Top staff</span>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => onNavigate('staff-perf')}>All →</button>
                    </div>
                    {topStaff.length === 0 ? (
                        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No staff data yet.</p>
                    ) : (
                        topStaff.map((s, i) => (
                            <RankRow
                                key={s.id}
                                rank={i + 1}
                                name={s.name}
                                count={s.count}
                                avgScore={s.avgScore}
                                sentimentCounts={s.sentimentCounts}
                            />
                        ))
                    )}
                </div>

                {/* Top locations */}
                <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>📍 Top locations</span>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => onNavigate('locations-perf')}>All →</button>
                    </div>
                    {topLocations.length === 0 ? (
                        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No location data yet.</p>
                    ) : (
                        topLocations.map((l, i) => (
                            <RankRow
                                key={l.id}
                                rank={i + 1}
                                name={l.name}
                                sub={l.city || undefined}
                                count={l.count}
                                avgScore={l.avgScore}
                                sentimentCounts={l.sentimentCounts}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* ── Recent activity ── */}
            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>🕐 Recent activity</span>
                    <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => onNavigate('responses')}>All →</button>
                </div>
                {recentResponses.length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No feedback yet.</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        {recentResponses.map((r, i) => (
                            <div key={r._id} style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '10px 0',
                                borderBottom: i < recentResponses.length - 1 ? '0.5px solid var(--border)' : 'none',
                            }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>
                  {r.feedbackType === 'audio' ? '🎙️' : (r.overallSentiment ? EMOJI_MAP[r.overallSentiment] : '📋')}
                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {/*{r.feedbackTaker?.name || 'Unknown staff'}*/}
                                        {r.feedbackTaker?.name || (r.customerName && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · {r.customerName}</span>) || 'Unknown staff'}
                                        {r.billId && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · {r.billId}</span>}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                                        {r.locationName && <span>📍 {r.locationName} · </span>}
                                        {new Date(r.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        {r.feedbackType === 'audio' && r.analysisStatus !== 'done' && (
                                            <span style={{ marginLeft: 6, color: '#8b5cf6' }}>
                        {r.analysisStatus === 'processing' ? '🔄 Analysing' : r.analysisStatus === 'pending' ? '⏳ Pending' : r.analysisStatus === 'failed' ? '❌ Failed' : ''}
                      </span>
                                        )}
                                    </div>
                                </div>
                                {r.overallSentiment && (
                                    <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 10, background: COLORS[r.overallSentiment] + '20', color: COLORS[r.overallSentiment], fontWeight: 600, flexShrink: 0 }}>
                    {EMOJI_MAP[r.overallSentiment]} {r.overallSentiment}
                  </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Quick actions ── */}
            <div style={{ marginTop: 24 }}>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-muted)', marginBottom: 12 }}>Quick actions</p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {[
                        { label: '🔗 Create share link', tab: 'links' },
                        { label: '📋 Add feedback form', tab: 'forms' },
                        { label: '👤 Add staff member', tab: 'staff' },
                        { label: '📍 Add location', tab: 'locations' },
                    ].map(a => (
                        <button
                            key={a.tab}
                            className="btn btn-outline"
                            style={{ fontSize: 13 }}
                            onClick={() => onNavigate(a.tab)}
                        >
                            {a.label}
                        </button>
                    ))}
                </div>
            </div>

        </div>
    );
}